import cx from 'classnames';
import Big from 'big.js';
import { useMemo, useState } from 'react';
import { gql, useMutation, useSuspenseFragment } from '@apollo/client';
import { MdContentCopy, MdContentPaste, MdDelete, MdOpenInNew, MdSettings, MdSync } from 'react-icons/md';
import { Toggle } from '../../common/Toggle';
import { toast } from 'react-toastify';
import Button from '../../common/Button';
import { 
  ExternalListingRow_ExternalListingFragment,
  ExternalListingRow_DeleteExternalListingMutation,
  ExternalListingRow_DeleteExternalListingMutationVariables,
  ListingsSection_AdFragment,
  ExternalListingRow_SyncExternalListingMutation,
  ExternalListingRow_SyncExternalListingMutationVariables,
} from '../../../../codegen/graphql';
import { syncToLeboncoin } from '../../../lib/leboncoin/leboncoin_syncAd';
import { useRequestManager } from '../../common/RequestManager';
import { postMessageToLeboncoinExt } from '../../../lib/leboncoin/LeboncoinExtApi';
import EditListingConfigurationModal from './EditListingConfigurationModal';
import { getPlatformColor } from '../../../utils/getPlatformColor';
import * as countryCodes from 'country-codes-list';
import dayjs from 'dayjs';
import { checkLeboncoinListingVariationsConfigurationCompleteness, createDefaultVariationsConfiguration, createListingConfigurationWithDefaults, filterVariationsByConfiguration, mergeVariationsConfiguration } from '../../../../common/listingVariationsConfiguration';
import { calculatePrices } from '../../../../common/pricesCalculation';
import { selectBestSupplierProductVariation } from '../../../../common/selectBestSuplierProductVariation';
import inheritMarginOrTargetPrice from '../../../../common/inheritMarginOrTargetPrice';
import { SALE_CHANNEL } from '../../../../common/salesChannels';
import PricesCalculation_Popover from '../../common/PricesCalculation_Popover';
import { EBAY_ATTRIBUTES_SECTION_FRAGMENT } from '../EbayAttributesSection';
import { getTranslatedValue } from '../../common/TranslatedValue';
import { LEBONCOIN_ATTRIBUTES_SECTION_FRAGMENT } from '../LeboncoinAttributesSection';

export const EXTERNAL_LISTING_ROW_FRAGMENT = gql`
  fragment ExternalListingRow_ExternalListing on AdExternalListing {
    id
    adId
    shopId
    lang
    externalId
    externalData
    shop {
      id
      title
      shopType
      shopKey
    }
    configuration {
      isMultiVariation
      supplierKey
      marginPercentOnBaseCost
      targetListingPriceIncludingVat
      useSingleVariationId
      listingPriceRoundingMethod
      variationsConfig {
        types
        values
        variations
      }
    }
    lastSyncAt
    ...EbayAttributesSection_listing
    ...LeboncoinAttributesSection_listing
  }
  ${EBAY_ATTRIBUTES_SECTION_FRAGMENT}
  ${LEBONCOIN_ATTRIBUTES_SECTION_FRAGMENT}
`;

export const DELETE_EXTERNAL_LISTING_ROW_MUTATION = gql`
  mutation ExternalListingRow_DeleteExternalListing($id: ID!) {
    deleteAdExternalListing(id: $id)
  }
`;

export const SYNC_EXTERNAL_LISTING_ROW_MUTATION = gql`
  mutation ExternalListingRow_SyncExternalListing($adExternalListingId: ID!) {
    syncExternalListing(adExternalListingId: $adExternalListingId) {
      process {
        id
      }
      result
      error
      logLines
    }
  }
`;

type ListingConfiguration = NonNullable<ExternalListingRow_ExternalListingFragment['configuration']>;

interface ExternalListingRowProps {
  externalListing: ExternalListingRow_ExternalListingFragment;
  adData: ListingsSection_AdFragment;
  onDeleted: () => void;
  copiedListingConfiguration: ListingConfiguration | null;
  copiedFromListingId: string | null;
  onCopyConfiguration: (config: ListingConfiguration, fromListingId: string) => void;
  onPasteConfiguration: (listingId: string) => void;
  isSelected?: boolean;
  onToggleSelect?: (listingId: string, selected: boolean) => void;
}

export function ExternalListingRow({
  externalListing,
  adData,
  onDeleted,
  copiedListingConfiguration,
  copiedFromListingId,
  onCopyConfiguration,
  onPasteConfiguration,
  isSelected,
  onToggleSelect,
}: ExternalListingRowProps) {
  const { trackRequest } = useRequestManager();
  const [showEditListingConfigurationModal, setShowEditListingConfigurationModal] = useState(false);
  
  const fragmentData = useSuspenseFragment<ExternalListingRow_ExternalListingFragment>({
    fragment: EXTERNAL_LISTING_ROW_FRAGMENT,
    fragmentName: 'ExternalListingRow_ExternalListing',
    from: externalListing,
  });

  const { data: listing } = fragmentData;

  const [deleteExternalListing] = useMutation<
    ExternalListingRow_DeleteExternalListingMutation,
    ExternalListingRow_DeleteExternalListingMutationVariables
  >(DELETE_EXTERNAL_LISTING_ROW_MUTATION);
  const [syncExternalListingMutation] = useMutation<
    ExternalListingRow_SyncExternalListingMutation,
    ExternalListingRow_SyncExternalListingMutationVariables
  >(SYNC_EXTERNAL_LISTING_ROW_MUTATION);

  /*
   * Listing configuration merge with defaults based on variations types and variations
   */
  const listingConfigurationWithDefaults = useMemo(() => {
    return createListingConfigurationWithDefaults(
      listing.configuration,
      adData.variationTypes,
      adData.variations,
    )
  }, [listing.configuration?.variationsConfig, adData.variationTypes, adData.variations]);

  /*
   * Check if the listing configuration is complete
   */
  const configurationCheck = useMemo(() => {
    if (listing.shop.shopType === 'leboncoin') {
      return checkLeboncoinListingVariationsConfigurationCompleteness({
        configuration: listingConfigurationWithDefaults,
        types: adData.variationTypes,
        variations: adData.variations,
      });
    }

    return {
      isValid: true,
      typesErrors: {},
      valuesErrors: {},
      variationsErrors: {},
    };
  }, [listingConfigurationWithDefaults, adData.variationTypes, adData.variations]);


  // Get external listing URL based on shop type
  const externalListingUrl = useMemo(() => {
    if (listing.shop.shopType === 'leboncoin') {
      return `https://www.leboncoin.fr/ad/accessoires_animaux/${listing.externalId}`;
    }
    if (listing.shop.shopType === 'ebay') {
      const env = import.meta.env.VITE_APP_ENV === 'local' ? 'www.sandbox' : 'www';
      const ext = listing.lang.toLowerCase();
      return `https://${env}.ebay.${ext}/itm/${listing.externalId}`;
    }
    if (listing.shop.shopType === 'temu') {
      return `https://www.temu.com/goods.html?goods_id=${listing.externalId}`;
    }
    return '#';
  }, [listing.shop.shopType, listing.externalId]);

  // Format last sync date
  const formattedLastSyncAt = useMemo(() => {
    if (!listing.lastSyncAt) return 'Never';
    return dayjs(parseInt(listing.lastSyncAt)).format('DD/MM/YYYY HH:mm');
  }, [listing.lastSyncAt]);

  // Handle delete
  const handleDelete = async () => {
    if (!confirm(`Are you sure you want to delete this ${listing.shop.shopType} listing?`)) {
      return;
    }

    // Delete from Leboncoin if shop type is leboncoin
    if (listing.shop.shopType === 'leboncoin') {
      await trackRequest(
        postMessageToLeboncoinExt(window, {
          action: 'delete-ads',
          adIds: [listing.externalId],
        }),
        {
          title: 'Deleting Leboncoin listing',
          isBlocking: false,
        }
      );
    }

    // Delete
    await trackRequest(
      deleteExternalListing({ variables: { id: listing.id } }),
      {
        title: 'Deleting listing',
        isBlocking: false,
      }
    );
  };

  // Handle open in new tab
  const handleOpenInNewTab = () => {
    if (externalListingUrl) {
      window.open(externalListingUrl, '_blank', 'noopener,noreferrer');
    }
  };

  const syncExternalListing = async () => {
    let syncFn: () => Promise<any>;
    if (listing.shop.shopType === 'leboncoin') {
      syncFn = () => syncToLeboncoin(adData.id, listing.id);
    } else if (listing.shop.shopType === 'ebay' || listing.shop.shopType === 'temu') {
      syncFn = () => syncExternalListingMutation({ variables: { adExternalListingId: listing.id } });
    } else {
      throw new Error('Unsupported shop type');
    }
    
    await trackRequest(
      syncFn(),
      {
        title: 'Syncing listing',
        isBlocking: false,
      }
    );
  };

  const countryMetadata = useMemo(() => {
    return countryCodes.findOne('countryCode', listing.lang);
  }, [listing.lang]);

  const minAndMaxPrices = useMemo(() => {
    let minPrices: null | ReturnType<typeof calculatePrices> = null;
    let maxPrices: null | ReturnType<typeof calculatePrices> = null;
    for (const variation of adData.variations) {
      const supplierProductVariation = selectBestSupplierProductVariation(
        listingConfigurationWithDefaults.supplierKey!,
        listing.lang,
        variation.supplierProductVariations,
      );
      if (!supplierProductVariation) {
        continue;
      }
      const calculatedCost = supplierProductVariation.calculatedCost.find((cc) => (
        cc.destinationCountry === listing.lang
      ))!;

      if (!calculatedCost || calculatedCost.totalCostExcludingVAT === 0) {
        continue;
      }

      const marginOrTargetPrices = inheritMarginOrTargetPrice({
        listing: listing,
        listingConfiguration: listingConfigurationWithDefaults,
        variation: variation,
        ad: adData,
      });
      const prices = calculatePrices({
        marginPercentOnBaseCost: marginOrTargetPrices.selected.marginPercentOnBaseCost,
        targetListingPriceIncludingVat: marginOrTargetPrices.selected.targetListingPriceIncludingVat,
        listingPriceRoundingMethod: marginOrTargetPrices.selected.listingPriceRoundingMethod,
        productCostExcludingVAT: calculatedCost.totalCostExcludingVAT,
        packingCostExcludingVAT: supplierProductVariation.packaging?.cost ?? 0,
        forPlatform: listing.shop.shopType as SALE_CHANNEL,
        supplierName: supplierProductVariation.supplierName,
        origin: 'FR',
        destination: listing.lang,
        packageWeight: supplierProductVariation.packaging?.weight ?? 0,
        packageSize1: supplierProductVariation.packaging?.size1 ?? 0,
        packageSize2: supplierProductVariation.packaging?.size2 ?? 0,
        packageSize3: supplierProductVariation.packaging?.size3 ?? 0,
      });
      if (
        !minPrices
        || prices.retailPriceIncludingVAT.lt(minPrices.retailPriceIncludingVAT)
      ) {
        minPrices = prices;
      }
      if (
        !maxPrices
        || prices.retailPriceIncludingVAT.gt(maxPrices.retailPriceIncludingVAT)
      ) {
        maxPrices = prices;
      }
    }
    return { minPrices, maxPrices };
  }, [listingConfigurationWithDefaults]);

  return (<>
    <tr className={cx('hover:bg-gray-50', isSelected && 'bg-blue-50')}>
      {onToggleSelect && (
        <td className="px-4 py-3 whitespace-nowrap">
          <Toggle
            label=""
            value={isSelected ?? false}
            onChange={(selected) => onToggleSelect(listing.id, selected)}
          />
        </td>
      )}
      <td className="px-4 py-3 whitespace-nowrap">
        <div className="flex flex-col">
          <span className="text-sm font-medium text-gray-900">
            {listing.shop.title}
          </span>
          <span
            className={cx(
              "text-xs font-semibold",
              getPlatformColor(listing.shop.shopType).textClass
            )}
          >
            {listing.shop.shopType}
            {listing.shop.shopKey ? ` · ${listing.shop.shopKey}` : ''}
          </span>
        </div>
      </td>
      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-700">
        {listing.lang} {countryMetadata?.flag}
      </td>
      <td className="px-4 py-3 whitespace-nowrap text-sm">
        {listing.externalId ? (
          <a href={externalListingUrl} target="_blank" rel="noopener noreferrer" className="text-blue-700 underline">
            #{listing.externalId}
          </a>
        ) : (
          <span className="text-gray-400">—</span>
        )}
      </td>
      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-700">
        {formattedLastSyncAt}
      </td>
      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-700">
        {listing.configuration?.supplierKey ?? '—'}
      </td>
      <td className="px-4 py-3 text-sm text-gray-700">
        {(() => {
          const isMultiVariation = listing.configuration?.isMultiVariation;
          if (isMultiVariation) {
            const filteredVariations = filterVariationsByConfiguration(
              adData.variations,
              listingConfigurationWithDefaults
            );
            const excludedCount = adData.variations.length - filteredVariations.length;
            return `${filteredVariations.length} included, ${excludedCount} excluded`;
          }

          const publishedVariation = adData.variations.find(v => v.id === listing.configuration?.useSingleVariationId);
          return publishedVariation
            ? `Single: ${publishedVariation.variationTypeValues.map(v => getTranslatedValue(v.value, listing.lang ?? 'FR')).join(', ')}`
            : 'Single variation';
        })()}
      </td>
      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-700">
        {minAndMaxPrices.minPrices && (
          <PricesCalculation_Popover
            prices={minAndMaxPrices.minPrices}
          />
        )}
        {minAndMaxPrices.maxPrices
        && (
          !minAndMaxPrices.minPrices
          || false === minAndMaxPrices.maxPrices.retailPriceExcludingVAT.eq(minAndMaxPrices.minPrices.retailPriceExcludingVAT)
        ) && (
          <PricesCalculation_Popover
            prices={minAndMaxPrices.maxPrices}
          />
        )}
      </td>
      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-700">
        {listing.shop.shopType === 'leboncoin' ? (listing.externalData?.location ?? '—') : '—'}
      </td>
      <td className="px-4 py-3 whitespace-nowrap text-right">
        <div className="flex justify-end gap-1">
          <Button
            onClick={handleDelete}
            preset="danger-light"
            size="sm"
            tooltip="Delete listing"
          >
            <MdDelete size={16} />
          </Button>
          <Button
            onClick={() => syncExternalListing()}
            preset="white"
            size="sm"
            tooltip="Sync listing"
          >
            <MdSync size={16} />
          </Button>
          <Button
            onClick={() => setShowEditListingConfigurationModal(true)}
            preset="white"
            size="sm"
            tooltip="Edit listing configuration"
          >
            <MdSettings size={16} />
          </Button>
          <Button
            onClick={() => listing.configuration && onCopyConfiguration(listing.configuration, listing.id)}
            preset="white"
            size="sm"
            tooltip="Copy listing configuration"
            disabled={!listing.configuration}
          >
            <MdContentCopy size={16} />
          </Button>
          {copiedListingConfiguration != null && listing.id !== copiedFromListingId && (
            <Button
              onClick={() => onPasteConfiguration(listing.id)}
              preset="white"
              size="sm"
              tooltip="Paste listing configuration"
            >
              <MdContentPaste size={16} />
            </Button>
          )}
          {externalListingUrl && listing.externalId && (
            <Button
              onClick={handleOpenInNewTab}
              preset="white"
              size="sm"
              tooltip="Open in new tab"
            >
              <MdOpenInNew size={16} />
            </Button>
          )}
        </div>
      </td>
    </tr>
    {showEditListingConfigurationModal && (
      <EditListingConfigurationModal
        adData={adData}
        listingConfigurationWithDefaults={listingConfigurationWithDefaults}
        configurationCheck={configurationCheck}
        listing={listing}
        onClose={() => setShowEditListingConfigurationModal(false)}
      />
    )}
  </>);
}

